/* eslint-disable tailwindcss/no-arbitrary-value */
'use client';

import { RewardProduct_Detail } from '__generated__/graphql';
import { useRouter } from 'next/navigation';
import { JSX, useEffect, useRef, useState } from 'react';
import { Translated } from 'utils/staticText';
import {
  trackingLeadCaptureAuthButton,
  trackLeadCaptureClick,
  MembershipRewardsTrackingData,
} from 'utils/tracking';
import { RewardHeaderStaticText } from './i18n';
import AccountCircle from '$/components/icons/AccountCircle';
import ArrowLeftAlt from '$/components/icons/ArrowLeftAlt';
import Diamond from '$/components/icons/Diamond';
import Help from '$/components/icons/Help';
import Logout from '$/components/icons/Logout';
import PersonRefferal from '$/components/icons/PersonRefferal';
import { useIdentity } from '$/modules/auth/hooks';

type HeaderProps = {
  authEndpoint: string;
  logo: JSX.Element;
  product: RewardProduct_Detail;
  staticText: Translated<typeof RewardHeaderStaticText>;
};

export function RewardHeader(props: HeaderProps): JSX.Element {
  const identity = useIdentity();
  const [showProfileMenu, setshowProfileMenu] = useState<boolean>(false);
  const [enableUserEarnPage, setEnableUserEarnPage] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const router = useRouter();
  const tracking: MembershipRewardsTrackingData = {
    campaignId: props.product.productId,
    campaignName: props.product.productDisplayName,
    product: {
      productUid: props.product?.catalogueProduct?.productUid,
      productName: props.product?.catalogueProduct?.productName,
      providerName: props.product?.catalogueProduct?.provider?.providerName,
    },
  };
  const logout = () => {
    trackLeadCaptureClick(
      'card',
      `Top Nav_Logout`,
      tracking,
      'lead-submission',
      1,
      'login_signup_interact',
      'Logout CTA',
    );
    const url = new URL(`${props.authEndpoint}/logout`, window.location.href);
    fetch(url, { method: 'POST', credentials: 'include' })
      .then((response) => {
        if (response.ok) {
          window.location.reload();
        } else {
          console.error('Logout failed:', response.statusText);
        }
      })
      .catch((error) => {
        console.error('Error during logout:', error.message);
      });
  };

  const profileAction = () => {
    if (!identity) {
      trackLeadCaptureClick(
        'card',
        `Top Nav_Login`,
        tracking,
        'lead-submission',
        1,
        'login_signup_interact',
        'Login CTA',
      );
      setTimeout(() => {
        router.push(
          `/login?redirect_uri=${encodeURIComponent(window.location.href)}`,
        );
      }, 300);
    } else {
      setshowProfileMenu(!showProfileMenu);
    }
  };

  const [givenName, setGivenName] = useState<string>('User');
  useEffect(() => {
    if (identity && identity.given_name) {
      setGivenName(identity.given_name);
    }
  }, [identity]);

  // to do : remove ?enableReferEarnPage (refer & earn page) when ready production
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const isUserEarnPageEnabled =
        urlParams.get('enableReferEarnPage') === 'true';
      setEnableUserEarnPage(isUserEarnPageEnabled);
    }
  }, []);

  // TODO: replace by useOutsideClick in future
  useEffect(() => {
    const handleClickOutsideMenu = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setshowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutsideMenu);
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideMenu);
    };
  }, []);

  const goBack = () => {
    trackingLeadCaptureAuthButton(
      'Back To Previous Step',
      '',
      'top-nav',
      'Top Nav_Back Arrow',
      tracking,
      false,
      'lead-submission',
      'reward_gateway',
    );
    const urlParams = new URLSearchParams(window.location.search);
    const ref = urlParams.get('ref');
    const isValidPath = ref && /^\/(?!\/)/.test(ref) && !ref.startsWith('http');
    switch (true) {
      case isValidPath:
        router.push(ref);
        break;
      case !!document.referrer:
        router.push(document.referrer);
        break;
      case window.history.length > 1:
        router.back();
        break;
      default:
        router.push('/');
        break;
    }
  };

  return (
    <header className="flex h-[--size-top-nav-1] flex-col items-center justify-center shadow-md bg-[--colors-neutral-white] md:h-[--size-top-nav-2] lg:h-[--size-top-nav-3] xl:h-[--size-top-nav-4]">
      <div className="flex h-full items-center self-stretch px-[--spacing-4]">
        <div className="mx-auto flex h-9 w-full items-center justify-between gap-2 md:h-10 md:w-[--general-narrow-content-max-width-2] md:max-w-[--general-narrow-content-max-width-2] lg:h-11 lg:w-[--breakpoints-content-narrow-3-md] lg:max-w-[--breakpoints-content-narrow-3-md] lg:gap-[--gap-6] xl:w-[--breakpoints-content-narrow-3-lg] xl:max-w-[--breakpoints-content-narrow-3-lg]">
          <button type="button" onClick={() => goBack()}>
            <ArrowLeftAlt className="size-[--shapes-icon-size-lg]" />
          </button>
          <div className="w-25 shrink-0 grow-0 md:w-[125px] lg:w-32">
            {props.logo}
          </div>
          <div className="relative">
            <button
              type="button"
              className="flex size-9 items-center justify-center rounded-full p-1 bg-[--fill-primary-light] text-[--fill-primary-light] lg:size-[--size-button-md]"
              onClick={profileAction}
            >
              <AccountCircle className="h-[--shapes-icon-size-lg] w-[--size-icon-sm] text-[--buttons-primary] lg:size-[--size-icon-md]" />
            </button>
            {showProfileMenu && (
              <div
                ref={menuRef}
                className="absolute right-0 flex w-[--general-3-col-width] flex-col items-end rounded-[--radius-card-inner] shadow-lg bg-[--fill-card]"
              >
                <div className="flex items-center gap-[--spacing-gap-x-sm] self-stretch px-[--spacing-padding-x] py-[--spacing-padding-y]">
                  <div className="flex flex-col items-start overflow-hidden text-ellipsis">
                    <div className="mhg-alpha-title-1 text-[--text-primary]">
                      {givenName}
                    </div>
                    <div className="mhg-alpha-body-2 w-full truncate text-[--text-secondary-lighter]">
                      {identity?.email}
                    </div>
                  </div>
                </div>
                <div className="mhg-alpha-border-default-y flex flex-col items-start self-stretch px-0 py-[--spacing-padding-y-sm]">
                  <a
                    className="mhg-alpha-button-1 flex h-[--button-size-regular] items-center gap-[--button-gap] self-stretch px-[--spacing-padding-x] py-0"
                    href={props.staticText.myRewards.link}
                  >
                    <Diamond className="size-[--shapes-icon-size-lg]" />{' '}
                    {props.staticText.myRewards.text}
                  </a>
                  {enableUserEarnPage && (
                    <a
                      className="mhg-alpha-button-1 flex h-[--button-size-regular] items-center gap-[--button-gap] self-stretch px-[--spacing-padding-x] py-0"
                      href={props.staticText.referAndEarn.link}
                    >
                      <PersonRefferal className="size-[--shapes-icon-size-lg]" />{' '}
                      {props.staticText.referAndEarn.text}
                    </a>
                  )}
                </div>
                <div className="flex flex-col items-start self-stretch px-0 py-[--spacing-padding-y-sm]">
                  <a
                    className="mhg-alpha-button-1 flex h-[--button-size-regular] items-center gap-[--button-gap] self-stretch px-[--spacing-padding-x] py-0"
                    href={props.staticText.helpCenter.link}
                  >
                    <Help className="size-[--shapes-icon-size-lg]" />{' '}
                    {props.staticText.helpCenter.text}
                  </a>
                  <button
                    className="mhg-alpha-button-1 flex h-[--button-size-regular] items-center gap-[--button-gap] self-stretch px-[--spacing-padding-x] py-0"
                    onClick={logout}
                    type="button"
                  >
                    <Logout className="size-[--shapes-icon-size-lg]" />{' '}
                    {props.staticText.logOut}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
